import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";

export type ComplianceStatus = "success" | "warning" | "danger";

interface ComplianceCardProps {
  title: string;
  description: string;
  status: ComplianceStatus;
  badgeLabel?: string;
  lastUpdated?: string;
  className?: string;
  icon?: LucideIcon;
}

const statusConfig = {
  success: {
    label: "Compliant",
    className: "bg-success text-success-foreground",
  },
  warning: {
    label: "Needs Attention",
    className: "bg-warning text-warning-foreground",
  },
  danger: {
    label: "Non-Compliant",
    className: "bg-danger text-danger-foreground",
  },
};

export function ComplianceCard({ 
  title, 
  description, 
  status,
  badgeLabel,
  lastUpdated,
  className,
  icon: Icon
}: ComplianceCardProps) {
  const config = statusConfig[status];

  return (
    <Card className={cn("bg-white dark:bg-white shadow-card transition-all hover:shadow-elevated", className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {Icon && <Icon className="h-6 w-6 text-gray-500" />}
            <CardTitle className="text-lg font-semibold text-gray-900">{title}</CardTitle>
          </div>
          <Badge className={config.className}>
            {badgeLabel || config.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm mb-2 font-medium text-gray-700">{description}</p>
        {lastUpdated && (
          <p className="text-xs text-gray-500">
            Last updated: {lastUpdated}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
